/**
 * Resource-pool bar + points pips for a player frame (#266). Rendered
 * directly under the HP bar (and above the cast bar — see `FrameCastBar`'s
 * "HP/resource bars" doc comment) in BOTH `OwnFrame` (self) and
 * `TargetFrame` (target/enemy) so any resource/points champion shows its
 * pools wherever that unit has a frame.
 *
 * Generalized, not Rogue-special-cased: ANY champion with a `resource`
 * (energy/mana/rage) gets the continuous bar; ANY champion with `points`
 * (combo points/holy power/...) gets the discrete pip row. Both are
 * independently omitted (no empty placeholder, no layout shift) when the
 * entity's wire value is `null` OR the champion has no known ceiling in
 * `resourceLabels.ts` — the two cases that mean "this champion has no such
 * pool" (wire-absent) or "we don't know its max yet" (table-absent).
 *
 * #344: the resource bar is COLOR-CODED by the champion's content
 * `resourceId` (rage → red, energy → yellow, mana → blue, unmapped/future →
 * light-purple placeholder), via `champions/resourceColors.ts` — the same
 * bar the above-head world-space display (`render/entityView.ts`, #337)
 * colors identically.
 *
 * PURELY COSMETIC / CLIENT-SIDE. Reads the already-wired per-entity
 * `resource`/`points` current values (`WireEntity.resource`/`.points`) plus
 * the client-only `max` lookup — no sim, no protocol behavior, no hash.
 */

import type { ReactElement } from "react";
import type { ChampionId } from "@pit/protocol";
import { pointsPipStates, resourceCeilings } from "../champions/resourceLabels.js";
import { resourceColorCss } from "../champions/resourceColors.js";
import { POINTS_PIP_EMPTY_CSS, POINTS_PIP_FILLED_CSS } from "../theme/palette.js";

export function FrameResourceBar({
  resource,
  points,
  championId,
  testIdPrefix,
}: {
  readonly resource: number | null;
  readonly points: number | null;
  readonly championId: ChampionId;
  /** data-testid prefix, e.g. "own" → "own-resource-bar"/"own-points". */
  readonly testIdPrefix: string;
}): ReactElement | null {
  const { maxResource, maxPoints, resourceId } = resourceCeilings(championId);
  const showResource = resource !== null && maxResource !== null && maxResource > 0;
  // #923: which pips are filled now comes from the SHARED rule in
  // `resourceLabels.ts` — the same one the above-head world-space pip row
  // draws from, so the two readouts of one pool can never disagree. `null`
  // still means "no pip row at all", exactly as the old inline predicate did.
  const pipStates = pointsPipStates(points, maxPoints);
  if (!showResource && pipStates === null) return null;
  return (
    <div>
      {!showResource ? null : (
        <ResourceBar
          current={resource as number}
          max={maxResource as number}
          color={resourceColorCss(resourceId)}
          testId={`${testIdPrefix}-resource-bar`}
        />
      )}
      {pipStates === null ? null : (
        <PointsPips
          states={pipStates}
          current={points as number}
          max={maxPoints as number}
          testId={`${testIdPrefix}-points`}
        />
      )}
    </div>
  );
}

function ResourceBar({
  current,
  max,
  color,
  testId,
}: {
  readonly current: number;
  readonly max: number;
  /** #344: the champion's resourceId color (`resourceColorCss`). */
  readonly color: string;
  readonly testId: string;
}): ReactElement {
  const f = max <= 0 ? 0 : Math.min(1, Math.max(0, current / max));
  return (
    <div
      data-testid={testId}
      data-current={String(current)}
      data-max={String(max)}
      style={{
        marginTop: 4,
        width: "100%",
        height: 6,
        background: "#111",
        border: "1px solid #333",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${f * 100}%`,
          height: "100%",
          background: color,
        }}
      />
    </div>
  );
}

function PointsPips({
  states,
  current,
  max,
  testId,
}: {
  /** #923: the shared `pointsPipStates` result — filled/empty per pip. */
  readonly states: readonly boolean[];
  /** Raw wire value, for the `data-current` readout only. */
  readonly current: number;
  readonly max: number;
  readonly testId: string;
}): ReactElement {
  const pips = states;
  return (
    <div
      data-testid={testId}
      data-current={String(current)}
      data-max={String(max)}
      style={{ marginTop: 4, display: "flex", gap: 3 }}
    >
      {pips.map((filled, i) => (
        <div
          // Fixed pip count per champion (`max`) — index is a stable key.
          key={i}
          data-testid={`${testId}-pip`}
          data-filled={filled ? "true" : "false"}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            border: "1px solid #555",
            background: filled ? POINTS_PIP_FILLED_CSS : POINTS_PIP_EMPTY_CSS,
          }}
        />
      ))}
    </div>
  );
}
